import React, { useMemo, useRef, useState } from "react";
import ReactAudioPlayer from "react-audio-player";
import "../styles/audio-player.scss";

function isIOSSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return (
    /iP(ad|hone|od)/.test(ua) &&
    /WebKit/.test(ua) &&
    !/CriOS|FxiOS|OPiOS|mercury/.test(ua)
  );
}

function formatTime(secs) {
  if (!secs || isNaN(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function StyledAudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
  }

  function handleTimeUpdate() {
    setCurrentTime(audioRef.current?.currentTime || 0);
  }

  function handleLoadedMetadata() {
    setDuration(audioRef.current?.duration || 0);
  }

  function handleEnded() {
    setPlaying(false);
    setCurrentTime(0);
  }

  function handleProgressClick(e) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * duration;
  }

  function toggleMute() {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !muted;
    setMuted(!muted);
  }

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="styled-audio-player">
      <audio
        ref={audioRef}
        src={src}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      {/* Play / Pause */}
      <button
        type="button"
        className="sap-btn sap-play-btn"
        onClick={togglePlay}
        title={playing ? "Pause" : "Play"}
      >
        <i className={`fa ${playing ? "fa-pause" : "fa-play"}`} />
      </button>

      {/* Elapsed */}
      <span className="sap-time sap-elapsed">{formatTime(currentTime)}</span>

      {/* Progress bar */}
      <div
        className="sap-track"
        onClick={handleProgressClick}
        title="Seek"
      >
        <div className="sap-track-bg" />
        <div className="sap-track-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Duration */}
      <span className="sap-time sap-duration">{formatTime(duration)}</span>

      {/* Volume / mute */}
      <button
        type="button"
        className="sap-btn sap-vol-btn"
        onClick={toggleMute}
        title={muted ? "Unmute" : "Mute"}
      >
        <i className={`fa ${muted ? "fa-volume-mute" : "fa-volume-up"}`} />
      </button>
    </div>
  );
}

export default function AudioPlayer({ src, autoPlay, styled }) {
  const audioSrc = useMemo(
    () => (src && typeof src !== "string" ? URL.createObjectURL(src) : src),
    [src],
  );
  const audioRef = useRef(null);
  const [error, setError] = useState(false);

  // Custom styled player
  if (styled) {
    return <StyledAudioPlayer src={audioSrc} />;
  }

  // iOS Safari: use only native controls, no custom play button
  if (isIOSSafari()) {
    return (
      <div className="ios-audio-player">
        <audio
          ref={audioRef}
          src={audioSrc}
          controls
          onError={() => setError(true)}
          style={{
            width: 326,
            height: 28,
            borderRadius: 4,
            background: "#f6f6f6",
          }}
        />
        {error && (
          <span style={{ color: "red", marginLeft: 8 }}>Voice Error</span>
        )}
      </div>
    );
  }

  // Other browsers: use react-audio-player
  return <ReactAudioPlayer src={audioSrc} autoPlay={autoPlay} controls />;
}
